import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { API } from '@/App';
import { CheckCircle, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      
      if (response.data.status === 'paid' || response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data
        window.location.reload();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(attempts + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 text-center">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-violet-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-heading font-bold mb-2">Processing Payment...</h2>
              <p className="text-slate-600">Please wait while we confirm your payment</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold mb-2 text-green-600">Payment Successful!</h2>
              <p className="text-slate-600 mb-6">Your subscription has been activated</p>
              <Button
                onClick={() => navigate(user?.account_type === 'broker' ? '/broker' : '/dashboard')}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                Go to Dashboard
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <h2 className="text-2xl font-heading font-bold mb-2 text-red-600">Payment Error</h2>
              <p className="text-slate-600 mb-6">There was an issue processing your payment</p>
              <Button
                onClick={() => navigate('/settings')}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                Try Again
              </Button>
            </>
          )}
          
          {status === 'timeout' && (
            <>
              <h2 className="text-2xl font-heading font-bold mb-2 text-yellow-600">Processing...</h2>
              <p className="text-slate-600 mb-6">Payment is taking longer than expected. Please check your email for confirmation.</p>
              <Button
                onClick={() => navigate(user?.account_type === 'broker' ? '/broker' : '/dashboard')}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                Continue
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;